import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { getCustomerAccountByAdmin } from "api/wallet";

const CURRENCIES = ["THB", "LAK", "USD"];
const SYM = { THB: "฿", LAK: "₭", USD: "$" };
const fmtCur = (n, c) => `${SYM[c] || ""}${Math.abs(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

// A clickable pill shown only when a customer's wallet balance is negative in at
// least one currency — surfaces owed debt during pickup/delivery collection, since
// ShippingPaymentDialog otherwise only reveals the balance after staff open it.
// Balance persists independent of the wallet's active flag, so this ignores
// walletActive on purpose (see ShippingPaymentDialog.jsx for the same balance fetch).
export default function WalletDebtBadge({ customerId, onClick }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [balances, setBalances] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!customerId) {
      setBalances(null);
      return;
    }
    let active = true;
    setLoading(true);
    getCustomerAccountByAdmin(customerId)
      .then((res) => {
        if (!active) return;
        const found = res && res.success && res.data;
        setBalances(
          found
            ? { THB: Number(res.data.THB) || 0, LAK: Number(res.data.LAK) || 0, USD: Number(res.data.USD) || 0 }
            : null
        );
      })
      .catch(() => {
        if (active) setBalances(null);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [customerId]);

  if (loading || !balances) return null;

  const negativeCurrencies = CURRENCIES.filter((c) => balances[c] < 0);
  if (negativeCurrencies.length === 0) return null;

  const amountsText = negativeCurrencies.map((c) => fmtCur(balances[c], c)).join(" / ");

  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        cursor: "pointer",
        border: `1.5px solid ${alpha(theme.palette.error.main, 0.4)}`,
        background: alpha(theme.palette.error.main, 0.08),
        borderRadius: "999px",
        px: 1.25,
        py: 0.4,
        "&:hover": { background: alpha(theme.palette.error.main, 0.14) },
      }}
    >
      <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 15, color: theme.palette.error.main }} />
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: theme.palette.error.main }}>
        {t("pickup.selfPickup.walletDebt.badge", { amounts: amountsText })}
      </Typography>
    </Box>
  );
}
